import { useState } from 'react'
import { Users, Check, X as XIcon } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { useToast } from '../stores/toastStore'
import { messaggioErrore } from '../lib/api'
import { useRispondiInvito, type InvitoTeam } from '../hooks/useInviti'

interface Props {
  inviti: InvitoTeam[]
  onChiudi: () => void
}

/**
 * Modale che appare al login se l'SDR ha inviti team in sospeso.
 *
 * Mostra UN invito alla volta. Quando l'utente accetta o rifiuta,
 * si passa al successivo; quando finiscono tutti la modale si chiude.
 * Il padre (AppShell) usa localStorage per non riproporre la stessa
 * modale finché lo stato non cambia (vedi `useEffect` lì).
 *
 * Il "Rispondi più tardi" chiude la modale ma NON segna gli inviti come
 * gestiti — restano in attesa e l'SDR vedrà di nuovo la modale al
 * prossimo refresh (o al prossimo login se ha già messo a tacere via
 * localStorage del padre).
 */
export function InvitoTeamModal({ inviti, onChiudi }: Props) {
  const toast = useToast()
  const { accetta, rifiuta } = useRispondiInvito()
  const [indice, setIndice] = useState(0)

  const invito = inviti[indice]
  if (!invito) {
    onChiudi()
    return null
  }

  const passaAlSuccessivo = (): void => {
    if (indice < inviti.length - 1) {
      setIndice((i) => i + 1)
    } else {
      onChiudi()
    }
  }

  const inCorso = accetta.isPending || rifiuta.isPending

  const onAccetta = async (): Promise<void> => {
    try {
      const res = await accetta.mutateAsync(invito.id)
      toast.successo(res.messaggio)
      passaAlSuccessivo()
    } catch (err) {
      toast.errore(messaggioErrore(err))
    }
  }

  const onRifiuta = async (): Promise<void> => {
    try {
      const res = await rifiuta.mutateAsync(invito.id)
      toast.info(res.messaggio)
      passaAlSuccessivo()
    } catch (err) {
      toast.errore(messaggioErrore(err))
    }
  }

  const nomeInvitante = invito.invitato_da
    ? `${invito.invitato_da.nome ?? ''} ${invito.invitato_da.cognome ?? ''}`.trim() || 'Il manager'
    : 'Il manager'

  return (
    <Modal aperto titolo="Invito al team" onChiudi={onChiudi} larghezzaMax={480}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'var(--accent-soft)', color: 'var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto',
        }}>
          <Users size={28} />
        </div>

        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
          <strong style={{ color: 'var(--text-primary)' }}>{nomeInvitante}</strong>{' '}
          ti ha invitato a unirti al team
        </p>

        <div style={{
          background: 'var(--accent-soft)',
          border: '1px solid var(--accent)',
          borderRadius: 'var(--radius-md)',
          padding: '14px 20px',
          textAlign: 'center',
        }}>
          <div style={{ color: 'var(--accent)', fontSize: 18, fontWeight: 700 }}>
            {invito.team.nome}
          </div>
        </div>

        {invito.messaggio && (
          <div style={{
            background: 'var(--bg-subtle)',
            borderLeft: '3px solid var(--accent)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
          }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
            }}>
              Messaggio
            </div>
            <div style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--text-primary)' }}>
              "{invito.messaggio}"
            </div>
          </div>
        )}

        {inviti.length > 1 && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
            Invito {indice + 1} di {inviti.length}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variante="ghost"
            iconaSinistra={<XIcon size={14} />}
            onClick={onRifiuta}
            disabled={inCorso}
            caricamento={rifiuta.isPending}
            style={{ flex: 1 }}
          >
            Rifiuta
          </Button>
          <Button
            variante="primary"
            iconaSinistra={<Check size={14} />}
            onClick={onAccetta}
            disabled={inCorso}
            caricamento={accetta.isPending}
            style={{ flex: 2 }}
          >
            Accetta invito
          </Button>
        </div>

        <button
          type="button"
          onClick={onChiudi}
          disabled={inCorso}
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--text-muted)', fontSize: 12,
            textDecoration: 'underline', cursor: inCorso ? 'not-allowed' : 'pointer',
            padding: 0,
          }}
        >
          Rispondi più tardi
        </button>
      </div>
    </Modal>
  )
}
